import { useContext } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router-dom'
import Flex from 'styled-flex-component'

import BrandFansGroupRowTrackedRoute from '@components/BrandFans/Group/Row/TrackedRoute'
import { BrandFanGroupMemberWarningContext } from '@context/BrandFanGroupMemberWarning'
import { EventTrackingNames } from '@enums'
import { IApplicationState } from '@store'
import {
  addBrandFanGroupMember,
  removeBrandFanGroupMember,
  selectBrandFanGroupHasLiveCampaigns,
  selectBrandFanGroupName,
  selectBrandFanMemberByGroupAndIdentity,
  selectHasPendingBrandFanGroupRequest,
} from '@store/brandFans'
import { selectBrandFansGroupId } from '@store/router'
import Theme from '@theme'
import { Container, H5 } from '@tribegroup'
import { GroupListItem } from './Groups.styled'
import BrandFansInfluencerIdentityGroupsItemAction from './ItemAction'

interface IProps {
  groupId: number
  identityId: number
  influencerId?: number
  isMemberOfGroup: boolean
  campaignId?: number
  submissionId?: number
}

const BrandFansInfluencerIdentityGroupsItem: React.FC<IProps> = ({
  groupId,
  identityId,
  campaignId,
  submissionId,
  influencerId,
}) => {
  const { showWarning, selectedGroupId } = useContext(BrandFanGroupMemberWarningContext)
  const match = useRouteMatch()
  const dispatch = useDispatch()
  const {
    brandFanMembership,
    currentGroup,
    groupName,
    hasLiveCampaigns,
    hasPendingRequest,
  } = useSelector((state: IApplicationState) => {
    const currentGroup = selectBrandFansGroupId(match)
    const brandFanMembership = selectBrandFanMemberByGroupAndIdentity(state, groupId, identityId)
    return {
      currentGroup,
      brandFanMembership,
      groupName: selectBrandFanGroupName(state, groupId),
      hasPendingRequest: selectHasPendingBrandFanGroupRequest(state, groupId),
      hasLiveCampaigns: selectBrandFanGroupHasLiveCampaigns(state, groupId),
    }
  })
  const isMemberOfGroup = Boolean(brandFanMembership)

  const onItemClick = () => {
    if (hasLiveCampaigns) {
      showWarning(groupId, isMemberOfGroup ? 'remove' : 'add')
    } else if (isMemberOfGroup) {
      dispatch(
        removeBrandFanGroupMember(identityId, groupId, {
          campaignId,
          influencerId,
          submissionId,
          memberId: brandFanMembership?.id,
          refetchIdentity: !currentGroup,
        }),
      )
    } else {
      dispatch(
        addBrandFanGroupMember(identityId, groupId, {
          campaignId,
          submissionId,
          influencerId,
          refetchIdentity: !currentGroup,
        }),
      )
    }
  }

  const groupsAlert = isMemberOfGroup
    ? 'brandFans.identityGroups.warning.removeFromLive'
    : 'brandFans.identityGroups.warning.addToLive'

  return (
    <BrandFansGroupRowTrackedRoute
      eventName={
        isMemberOfGroup
          ? EventTrackingNames.BrandFansGroupsRemove
          : EventTrackingNames.BrandFansGroupsAdd
      }
      groupId={groupId}
      additionalProperties={{
        identity_id: identityId,
        influencer_id: influencerId,
        groups_alert: hasLiveCampaigns ? groupsAlert : undefined,
      }}
    >
      <GroupListItem
        relative
        onClick={!hasPendingRequest ? onItemClick : undefined}
        topInnerSpacing={0.75}
        bottomInnerSpacing={0.75}
        leftInnerSpacing={1}
        rightInnerSpacing={1}
        bottomOuterSpacing={0.125}
        warning={selectedGroupId === groupId}
        data-testid={`brand-fan-group-item-${groupId}`}
      >
        <Flex full justifyBetween>
          <Container maxWidth={18} noWrap>
            <H5 title={groupName} textOverflowAsEllipsis color={Theme.grey900Color}>
              {groupName}
            </H5>
          </Container>
          <BrandFansInfluencerIdentityGroupsItemAction
            groupId={groupId}
            isMemberOfGroup={isMemberOfGroup}
          />
        </Flex>
      </GroupListItem>
    </BrandFansGroupRowTrackedRoute>
  )
}

export { BrandFansInfluencerIdentityGroupsItem }
export default BrandFansInfluencerIdentityGroupsItem
